import { userPreferencesApiConnector } from 'apiConnector/userPreferences/userPreferences';
import type { ProductVariationValidSyntax } from 'models/productsReferential';
import { firstValueFrom } from 'rxjs';
import {
  getProductVariationKey,
  getUserPreferenceProductLayoutPreferences,
  getUserPreferencesPinnedProductsVariations,
} from 'state/selectors';
import type { Thunk } from 'state/state.common';
import {
  userPreferencesEssentialPinnedProductsVariationsChanged,
  userPreferencesPinnedProductsVariationsChanged,
  userPreferencesProductLayoutUpdateSucceeded,
} from 'state/userPreferences/userPreferences.action';
import {
  emptyProductLayoutPreferences,
  type ProductLayoutOverride,
  type ProductLayoutPreferences,
  type UserPreferencesPinnedProductsVariationsChangedParams,
} from 'state/userPreferences/userPreferences.model';
import { isEqual } from 'utils/productVariation';

export function userPreferencesPinnedProductsVariationsChangedThunk({
  action,
  productVariation,
  workspace,
}: UserPreferencesPinnedProductsVariationsChangedParams): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    const pinnedProducts = getUserPreferencesPinnedProductsVariations(workspace)(state);

    const reduxAction =
      workspace === 'essential'
        ? userPreferencesEssentialPinnedProductsVariationsChanged
        : userPreferencesPinnedProductsVariationsChanged;

    switch (action) {
      case 'pin':
        dispatch(reduxAction([...pinnedProducts, productVariation]));
        break;
      case 'unpin':
        dispatch(reduxAction(pinnedProducts.filter(pinnedProduct => !isEqual(pinnedProduct, productVariation))));
        break;
      default:
        break;
    }
  };
}

export function updateUserPreferenceProductLayoutThunk(
  productVariation: ProductVariationValidSyntax,
  productLayoutOverride: ProductLayoutOverride | null,
): Thunk<void> {
  return async (dispatch, getState, { httpClient }) => {
    const productKey = getProductVariationKey(productVariation);
    const currentPreferences = getUserPreferenceProductLayoutPreferences(getState()) ?? emptyProductLayoutPreferences;

    let updatedPreferences: ProductLayoutPreferences;
    if (productLayoutOverride === null) {
      const { [productKey]: _removed, ...remainingLayouts } = currentPreferences.layoutsByProduct;
      updatedPreferences = {
        ...currentPreferences,
        layoutsByProduct: remainingLayouts,
      };
    } else {
      updatedPreferences = {
        ...currentPreferences,
        layoutsByProduct: {
          ...currentPreferences?.layoutsByProduct,
          [productKey]: productLayoutOverride,
        },
      };
    }

    await firstValueFrom(userPreferencesApiConnector(httpClient).updateUserPreferenceProductLayout(updatedPreferences));
    dispatch(userPreferencesProductLayoutUpdateSucceeded(updatedPreferences));
  };
}
